// Iter 63 — Compare From Findings: turn a table row into a comparable object identity and
// build the exact chat question the Iter 60 compare pipeline understands. Pure functions.

export const COMPARE_AGENT_ID = "code-analyst";
export const COMPARE_QUESTION_PARAM = "q";
export const COMPARE_WORKSPACE_PARAM = "ws";

const PROGRAM_TYPES = new Set(["*PGM", "*SRVPGM", "*MODULE"]);
const FILE_ATTR_KIND = { PF: "physical-file", LF: "logical-file", DSPF: "display-file" };
const KIND_WORD = {
  "physical-file": "physical file",
  "logical-file": "logical file",
  "display-file": "display file",
  file: "file",
  program: "program",
  field: "field",
};

const up = (v) => (v === null || v === undefined ? "" : String(v).trim().toUpperCase());

/** Normalise a row from Findings / Metadata into { key, workspaceId, library, name, kind, file, qualified, label } or null. */
export function normalizeComparableObject(row, ctx = {}) {
  if (!row || typeof row !== "object") return null;
  const entity = ctx.entity || row.entity || null;
  const workspaceId = ctx.workspaceId || row.workspace_id || null;
  const workspaceName = ctx.workspaceName || row.workspace_name || null;
  let library = "";
  let name = "";
  let kind = null;
  let file = "";
  switch (entity) {
    case "physical-files":
    case "occurrence": // Findings drill-down rows (DSPFD occurrences of a file name)
      library = up(row.library_name); name = up(row.file_name); kind = "physical-file"; break;
    case "logical-files":
      library = up(row.library_name); name = up(row.logical_name); kind = "logical-file"; break;
    case "programs":
      library = up(row.library_name); name = up(row.program_name); kind = "program"; break;
    case "rule-program": // Findings knowledge-base rule rows (program name only)
      library = up(row.program_library); name = up(row.program_name); kind = "program"; break;
    case "objects": {
      const t = up(row.object_type);
      if (PROGRAM_TYPES.has(t)) kind = "program";
      else if (t === "*FILE") kind = FILE_ATTR_KIND[up(row.attribute)] || "file";
      else return null;
      library = up(row.library_name); name = up(row.object_name); break;
    }
    case "fields":
      library = up(row.library_name); file = up(row.file_name); name = up(row.field_name); kind = "field"; break;
    default:
      return null;
  }
  if (!name || !workspaceId) return null;
  const lib = library || null;
  const qualified = kind === "field"
    ? `${lib ? `${lib}/` : ""}${file ? `${file}.` : ""}${name}`
    : lib ? `${lib}/${name}` : name;
  return {
    key: `${workspaceId}|${kind}|${qualified}`,
    workspaceId,
    workspaceName,
    library: lib,
    name,
    kind,
    file: file || null,
    qualified,
    label: kind === "field" ? `field ${qualified}` : qualified,
  };
}

export function canCompareObject(row, ctx) {
  return normalizeComparableObject(row, ctx) !== null;
}

/** The exact token for one side: fields are always typed; a type word is added only when both sides share LIB/NAME. */
export function compareTerm(obj, other) {
  const collides = other && other.qualified === obj.qualified && other.kind !== obj.kind;
  return obj.kind === "field" || collides ? `${KIND_WORD[obj.kind] || "object"} ${obj.qualified}` : obj.qualified;
}

export function makeCompareQuestion(left, right) {
  if (!left || !right) return null;
  return `Compare ${compareTerm(left, right)} and ${compareTerm(right, left)}.`;
}

export function compareChatPath(left, right, agentId = COMPARE_AGENT_ID) {
  const q = makeCompareQuestion(left, right);
  if (!q) return null;
  const params = new URLSearchParams();
  params.set(COMPARE_QUESTION_PARAM, q);
  params.set(COMPARE_WORKSPACE_PARAM, left.workspaceId);
  return `/chat/${agentId}?${params.toString()}`;
}

/** Why `candidate` cannot join `first` in one comparison — null when it can. */
export function selectionProblem(first, candidate) {
  if (!candidate) return "This row cannot be compared.";
  if (!first || first.key === candidate.key) return null;
  if (first.workspaceId !== candidate.workspaceId) return "Both objects must belong to the same workspace.";
  if (first.kind === "field" && candidate.kind === "field" && first.name === candidate.name) {
    return "Chat resolves fields by name — pick two different field names.";
  }
  return null;
}
